import React from 'react';
import { motion } from 'framer-motion';
import { XCircle, CheckCircle2, HeartHandshake, Frown, Smile } from 'lucide-react';
const problems = ['Tu hijo se aburre cuando intenta escribir.', 'Se distrae fácilmente.', 'Prefiere las pantallas antes que aprender.', 'No sabés qué actividades utilizar.', 'Terminan frustrados los dos.', 'Sentís que no avanzan.'];
const benefits = ['Mejora la escritura.', 'Desarrolla la motricidad fina.', 'Favorece la concentración.', 'Reduce el aburrimiento.', 'Incrementa la confianza del niño.', 'Fácil de aplicar en casa o en el aula.'];
export function ProblemSolutionSection() {
  return <>
      {/* SECTION 2: THE PROBLEM */}
      <section className="py-20 bg-white" data-id="element-92">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-93">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-94">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark mb-6" data-id="element-95">
              ¿Te identificás con alguna de estas situaciones?
            </h2>
            <p className="text-xl text-brand-text font-medium" data-id="element-96">
              No estás sola. Miles de mamás viven exactamente lo mismo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-97">
            {problems.map((problem, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-red-50 p-6 rounded-2xl border border-red-100 flex items-start gap-4" data-id="element-98">
                <XCircle className="text-red-500 shrink-0 mt-1" size={24} data-id="element-99" />
                <p className="text-brand-dark font-semibold text-lg" data-id="element-100">
                  {problem}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION 3: THE SOLUTION */}
      <section className="py-20 bg-brand-mint/20 relative overflow-hidden" data-id="element-101">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-102">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-103">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-104">
              <div className="relative" data-id="element-105">
                <div className="absolute inset-0 bg-brand-mint rounded-full blur-3xl opacity-50" data-id="element-106"></div>
                <img src="https://images.unsplash.com/photo-1588072432836-e10032774350?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Madre e hijo aprendiendo juntos" className="relative rounded-3xl shadow-soft border-4 border-white transform -rotate-2" data-id="element-107" />
                <div className="absolute -bottom-8 -right-8 bg-white p-4 rounded-full shadow-lg" data-id="element-108">
                  <HeartHandshake size={48} className="text-brand-primary" data-id="element-109" />
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-110">
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark mb-6" data-id="element-111">
                La forma <span className="text-brand-primary" data-id="element-112">divertida</span>{' '}
                de aprender a escribir
              </h2>
              <p className="text-lg text-brand-text mb-8" data-id="element-113">
                <strong data-id="element-114">Método Escribo Jugando</strong> transforma el
                aprendizaje en una experiencia entretenida mediante juegos,
                desafíos, actividades prácticas y ejercicios progresivos.
              </p>

              <div className="space-y-4" data-id="element-115">
                {benefits.map((benefit, index) => <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-slate-100" data-id="element-116">
                    <CheckCircle2 className="text-green-500 shrink-0" size={24} data-id="element-117" />
                    <span className="font-bold text-brand-dark" data-id="element-118">{benefit}</span>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 5: WITH VS WITHOUT */}
      <section className="py-20 bg-slate-50" data-id="element-119">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-120">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12" data-id="element-121">
            {/* Without */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-3xl p-8 shadow-sm border-t-8 border-red-400" data-id="element-122">
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-100" data-id="element-123">
                <div className="bg-red-100 p-3 rounded-full" data-id="element-124">
                  <Frown className="text-red-500" size={32} data-id="element-125" />
                </div>
                <h3 className="text-2xl font-bold text-brand-dark" data-id="element-126">
                  SIN EL MÉTODO
                </h3>
              </div>
              <ul className="space-y-5" data-id="element-127">
                {['Actividades aburridas.', 'Falta de motivación.', 'Distracciones constantes.', 'Poco avance.', 'Frustración.'].map((item, i) => <li key={i} className="flex items-center gap-3 text-lg font-medium text-slate-600" data-id="element-128">
                    <XCircle className="text-red-400 shrink-0" size={24} data-id="element-129" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            {/* With */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-3xl p-8 shadow-soft border-t-8 border-green-400 relative transform md:-translate-y-4" data-id="element-130">
              <div className="absolute -top-4 -right-4 bg-brand-yellow text-brand-dark font-bold px-4 py-1 rounded-full shadow-md transform rotate-12" data-id="element-131">
                ¡El cambio!
              </div>
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-100" data-id="element-132">
                <div className="bg-green-100 p-3 rounded-full" data-id="element-133">
                  <Smile className="text-green-500" size={32} data-id="element-134" />
                </div>
                <h3 className="text-2xl font-bold text-brand-dark" data-id="element-135">
                  CON EL MÉTODO
                </h3>
              </div>
              <ul className="space-y-5" data-id="element-136">
                {['Aprendizaje divertido.', 'Más atención.', 'Mayor motivación.', 'Mejor escritura.', 'Más confianza.'].map((item, i) => <li key={i} className="flex items-center gap-3 text-lg font-bold text-brand-dark" data-id="element-137">
                    <CheckCircle2 className="text-green-500 shrink-0" size={24} data-id="element-138" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}